import Model from 'ducker-model';
import { numeral } from '@fund/basic-lib/dist/lib/numeral';
import isEmpty from '@fund/basic-lib/dist/lib/lang/isEmpty';

// 1.定义property
const property = {
  fundCode: String,
  backCode: String,
  fundName: String,
  isDelListedFund: String, // 是否退市基金
  delListedDate: String, // 退市日期
  canBuy: String, // 是否可购买
  canInvest: String, // 是否可定投
  netValue: Number, // 最新净值
  historyNetValue: Number, // 历史净值
  netValueDate: String, // 最新净值日期
  riseDay: Number, // 日涨幅
  rateYear: Number, // 年收益率
  rateSetUp: Number, // 成立以来收益率
  isNewFund: String, // 是否新基金
  isCurrencyFund: String, // 是否货币基金
  isETF: String, // 是否ETF
  isGoldFund: String, // 是否黄金基金
  isPensionFund: String, // 是否养老基金
  isFofFund: String, // 是否FOF基金
  isQDIIFund: String, // 是否QDII基金
  isBondFund: String, // 是否债券基金
  isNetValueManageWealthCurrencyFund: String, // 是否净值理财货币基金
  isInterbankCertificateOfDeposit: String, // 是否同业存单
  isReitsFund: String, // 是否REITS基金
  isIndexFund: String, // 是否指数基金
  isActiveEquityFund: String, // 是否是主动权益类基金
  isETFFeederFund: String, // 是否ETF联接基金
  isHQBFund: String, // 是否是活期宝基金
  startTime: String, // 开始时间
  endTime: String, // 结束时间
  establishDate: String, // 成立时间
  newFundBuyStatus: String, // 新发基金购买状态
  bagType: String, // 是否宝类基金
  isSales: String, // 是否代销0
  bType: String, // 二级分类
  feature: String,
  pType: String,
  fType: String,
  fundType: String,
  isExchg: String,
};

// 2.定义replacedKeyFromPropertyName
const replacedKeyFromPropertyName = {
  fundCode: 'QDCODE',
  backCode: 'BACKCODE',
  fundName: 'SHORTNAME',
  isDelListedFund: { // 是否退市基金
    property: 'TSRQ',
    computed: (value) => !isEmpty(value),
  },
  delListedDate: 'TSRQ',
  canBuy: { // 是否可购买
    property: 'ISBUY',
    computed: (value) => value && [1, 2, 3, 8, 9].indexOf(parseInt(value, 10)) >= 0,
  },
  canInvest: { // 是否可定投
    property: 'DTZT',
    computed: (value) => value === '1',
  },
  netValue: { // 最新净值
    property: 'DWJZ',
    computed: (value) => (isEmpty(value) ? '--' : numeral(value).format('0.0000')),
  },
  historyNetValue: { // 历史净值
    property: 'LJJZ',
    computed: (value) => (isEmpty(value) ? '--' : numeral(value).format('0.0000')),
  },
  netValueDate: 'FSRQ', // 最新净值日期
  riseDay: { // 日涨幅
    property: 'RZDF',
    defaultValue: '--',
    computed: (value) => (isEmpty(value) ? '--' : numeral(value).format('0.00')),
  },
  rateYear: { // 年收益率
    property: 'SYL_1N',
    defaultValue: '--',
    computed: (value) => (isEmpty(value) ? '--' : `${numeral(value).format('0.00')}%`),
  },
  rateSetUp: { // 成立以来收益率
    property: 'SYL_LN',
    computed: (value) => (isEmpty(value) ? '--' : `${numeral(value).format('0.00')}%`),
  },
  isNewFund: { // 是否新基金
    property: 'ISNEW',
    computed: (value) => value === '1',
  },
  isCurrencyFund: { // 是否货币基金
    property: ['FUNDTYPE', 'FEATURE'],
    computed: ([fundType, feature]) => (['005', '004'].indexOf(fundType) > -1 && (isEmpty(feature) || feature.indexOf('200') < 0)),
  },
  isETF: { // 是否ETF
    property: ['FUNDTYPE', 'ISEXCHG', 'FEATURE'],
    computed: ([fundType, isExchg, feature]) => (fundType === '100' || parseInt(isExchg, 10) === 1 || feature?.indexOf('010') >= 0),
  },
  isGoldFund: { // 是否黄金基金
    property: 'FEATURE',
    computed: (value) => value?.indexOf('412') >= 0,
  },
  isPensionFund: { // 是否养老基金
    property: 'FEATURE',
    computed: (value) => value?.indexOf('510') >= 0,
  },
  isFofFund: { // 是否养老基金
    property: 'FEATURE',
    computed: (value) => value?.indexOf('030') >= 0 && value?.indexOf('031') < 0,
  },
  isQDIIFund: { // 是否QDII基金
    property: 'FUNDTYPE',
    computed: (value) => value === '007',
  },
  isBondFund: { // 是否债券基金
    property: 'FUNDTYPE',
    computed: (value) => ['003', '008'].indexOf(value) >= 0,
  },
  isNetValueManageWealthCurrencyFund: { // 是否净值理财货币基金
    property: 'FEATURE',
    computed: (value) => value?.indexOf('510') >= 0,
  },
  isInterbankCertificateOfDeposit: { // 是否同业存单
    property: 'FEATURE',
    computed: (value) => value?.indexOf('801') >= 0,
  },
  isReitsFund: { // 是否REITS基金
    property: 'FTYPE',
    computed: (value) => `${value}`.toUpperCase() === 'REITS',
  },
  isIndexFund: { // 是否指数基金
    property: 'FEATURE',
    computed: (value) => value?.indexOf('050') >= 0 || value?.indexOf('318') >= 0,
  },
  isActiveEquityFund: { // 是否是主动权益类基金
    property: 'BTYPE',
    computed: (value) => ['01', '21', '22', '23', '24'].indexOf(value) >= 0,
  },
  isETFFeederFund: { // 是否ETF联接基金
    property: 'FEATURE',
    computed: (value) => value?.indexOf('031') >= 0,
  },
  isHQBFund: { // 是否是活期宝基金
    property: 'BAGTYPE',
    computed: (value) => value === '1',
  },
  startTime: 'ISSBDATE', // 开始时间
  endTime: 'ISSEDATE', // 结束时间
  establishDate: { // 成立时间
    property: 'ESTABDATE',
    computed: (value) => (isEmpty(value) ? '--' : value),
  },
  newFundBuyStatus: { // 新发基金购买状态
    property: ['ISSBDATE', 'ISSEDATE'],
    computed: ([startTime, endTime]) => {
      const startDate = new Date(startTime?.replace(/-/g, '/')).getTime();
      const endDate = new Date(endTime?.replace(/-/g, '/')).getTime();
      const now = new Date().getTime();
      if (now < startDate) { // 还没开售新发
        return '等待开售';
      }
      if (now > endDate) {
        // 还没开售新发
        return '等待成立';
      }
      return '购买';
    },
  },
  bagType: {
    property: 'BAGTYPE',
    computed: (value) => (isEmpty(value) ? 0 : value - 0),
  },
  isSales: {
    property: 'ISSALES',
    computed: (value) => value === '1',
  },
  bType: 'BTYPE',
  feature: 'FEATURE',
  pType: 'PTYPE',
  fType: 'FTYPE',
  fundType: 'FUNDTYPE',
  isExchg: 'ISEXCHG',
};

export default new Model(property, replacedKeyFromPropertyName);
